import React, { createContext, useContext, FC } from 'react';
import { useManageTourProvider } from '@components/tours/hooks';
import { GlobalTourOverlay } from './GlobalTourOverlay';
import { globalTourConfigs } from '@components/tours/config';
import { isWeb } from '@helpers';
import type { TourContextValue, TourProviderProps } from '@components/tours/types';

const TourContext = createContext<TourContextValue | null>(null);

export const TourProvider: FC<TourProviderProps> = ({ children }) => {
  const value = useManageTourProvider();

  // Get current segment name for overlay
  const getSegmentName = () => {
    if (!value.globalTourProgress) return undefined;
    const tourConfig = globalTourConfigs[value.globalTourProgress.tourId];
    const segment = tourConfig?.segments[value.globalTourProgress.segmentIndex];
    return segment?.displayName || segment?.route;
  };

  const totalSegments = value.globalTourProgress
    ? globalTourConfigs[value.globalTourProgress.tourId]?.segments.length || 0
    : 0;

  return (
    <TourContext.Provider value={value}>
      {children}
      {isWeb && value.isGlobalTourActive && (
        <GlobalTourOverlay
          phase={value.globalTourPhase}
          currentSegment={value.globalTourProgress?.segmentIndex || 0}
          totalSegments={totalSegments}
          segmentName={getSegmentName()}
        />
      )}
    </TourContext.Provider>
  );
};

export const useTourContext = (): TourContextValue => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
};

export default TourProvider;
